import { alpha } from '@mui/material/styles';

// JLR LUXURY COLORS - Inspired by Range Rover and Jaguar premium aesthetics
const JLR_PRIMARY = {
  lighter: '#e8f2e8',
  light: '#c5d9c5',
  main: '#2d4a2d', // Deep forest green - Range Rover signature
  dark: '#1a2d1a',
  darker: '#0f1a0f',
};

const JLR_SECONDARY = {
  lighter: '#f5f3f0',
  light: '#e8e3dc',
  main: '#c8b99c', // Warm champagne gold
  dark: '#9d8b6f',
  darker: '#6b5e4c',
};

const JLR_ACCENT = {
  lighter: '#e6f3ff',
  light: '#b3daff',
  main: '#0066cc', // Jaguar racing blue
  dark: '#004499',
  darker: '#002266',
};

// LUXURY METALLIC TONES
const PLATINUM = {
  lighter: '#f8f9fa',
  light: '#e9ecef',
  main: '#6c757d',
  dark: '#495057',
  darker: '#212529',
};

const COPPER = {
  lighter: '#fdf2e9',
  light: '#f4d5b7',
  main: '#d4941e',
  dark: '#a17216',
  darker: '#6e4f0f',
};

// PREMIUM SYSTEM COLORS
const LUXURY_SUCCESS = {
  lighter: '#e8f5e8',
  light: '#c3e6c3',
  main: '#27ae60', // Elegant emerald
  dark: '#1e8449',
  darker: '#145a32',
};

const LUXURY_WARNING = {
  lighter: '#fff8e1',
  light: '#ffecb3',
  main: '#ffa726', // Sophisticated amber
  dark: '#ef6c00',
  darker: '#bf360c',
};

const LUXURY_ERROR = {
  lighter: '#fdf2f2',
  light: '#f8d7da',
  main: '#dc3545', // Refined crimson
  dark: '#c82333',
  darker: '#721c24',
};

const LUXURY_INFO = {
  lighter: '#e3f2fd',
  light: '#90caf9',
  main: '#1976d2', // Classic royal blue
  dark: '#1565c0',
  darker: '#0d47a1',
};

// Standard colors for compatibility
const PRIMARY = {
  lighter: '#c1e0d3',
  light: '#a8d4c1',
  main: '#82C1A7',
  dark: '#5b8775',
  darker: '#416154',
};
const SECONDARY = {
  lighter: '#fad4af',
  light: '#f8c38f',
  main: '#f5a95f',
  dark: '#ac7643',
  darker: '#7b5530',
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
};
const ERROR = {
  lighter: '#f2bbbc',
  light: '#eca0a1',
  main: '#e47778',
  dark: '#a05354',
  darker: '#723c3c',
};

// LUXURY GREY PALETTE - Sophisticated neutrals
const LUXURY_GREY = {
  0: '#FFFFFF',
  50: '#FAFBFC',
  100: '#F4F6F8',
  200: '#E9ECEF',
  300: '#DEE2E6',
  400: '#CED4DA',
  500: '#ADB5BD',
  600: '#6C757D',
  700: '#495057',
  800: '#343A40',
  900: '#212529',
  950: '#1A1D20',
  500_8: alpha('#ADB5BD', 0.08),
  500_12: alpha('#ADB5BD', 0.12),
  500_16: alpha('#ADB5BD', 0.16),
  500_24: alpha('#ADB5BD', 0.24),
  500_32: alpha('#ADB5BD', 0.32),
  500_48: alpha('#ADB5BD', 0.48),
  500_56: alpha('#ADB5BD', 0.56),
  500_80: alpha('#ADB5BD', 0.8),
};

// Standard GREY for compatibility
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  divider: '#1C1C21' ,
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export const defaultPalette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: {
      paper: '#E6E4E4',
      default: '#FFFFFF',
      neutral: GREY[500_16],
    },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: {
      paper: '#191712',
      default: '#33312B',
      neutral: GREY[500_16],
    },
    action: { active: GREY[500], ...COMMON.action },
  },
};

// JLR LUXURY THEME PALETTES
const JLR_COMMON = {
  common: { 
    black: '#000000', 
    white: '#ffffff' 
  },
  primary: { 
    ...JLR_PRIMARY, 
    contrastText: '#ffffff' 
  },
  secondary: { 
    ...JLR_SECONDARY, 
    contrastText: '#2d4a2d' 
  },
  accent: { 
    ...JLR_ACCENT, 
    contrastText: '#ffffff' 
  },
  info: { 
    ...LUXURY_INFO, 
    contrastText: '#ffffff' 
  },
  success: { 
    ...LUXURY_SUCCESS, 
    contrastText: '#ffffff' 
  },
  warning: { 
    ...LUXURY_WARNING, 
    contrastText: '#2d4a2d' 
  },
  error: { 
    ...LUXURY_ERROR, 
    contrastText: '#ffffff' 
  },
  platinum: { 
    ...PLATINUM, 
    contrastText: '#ffffff' 
  },
  copper: { 
    ...COPPER, 
    contrastText: '#ffffff' 
  },
  grey: LUXURY_GREY,
  divider: alpha('#2d4a2d', 0.12),
  action: {
    hover: alpha('#2d4a2d', 0.04),
    selected: alpha('#2d4a2d', 0.08),
    disabled: alpha('#2d4a2d', 0.26),
    disabledBackground: alpha('#2d4a2d', 0.12),
    focus: alpha('#2d4a2d', 0.12),
    hoverOpacity: 0.04,
    disabledOpacity: 0.38,
  },
};

export const jlrPalette = {
  light: {
    ...JLR_COMMON,
    mode: 'light',
    text: { 
      primary: '#2d4a2d', 
      secondary: LUXURY_GREY[700], 
      disabled: LUXURY_GREY[500] 
    },
    background: {
      paper: '#ffffff',
      default: '#fafbfc', // Subtle off-white
      neutral: alpha('#2d4a2d', 0.03),
      surface: '#f8f9fa',
      elevated: '#ffffff',
    },
    action: { 
      active: LUXURY_GREY[600], 
      ...JLR_COMMON.action 
    },
    shadows: {
      primary: alpha('#2d4a2d', 0.16),
      secondary: alpha('#c8b99c', 0.12),
      elevated: '0 8px 40px rgba(45, 74, 45, 0.12)',
    },
  },
  dark: {
    ...JLR_COMMON,
    mode: 'dark',
    text: { 
      primary: '#FFFFFF', 
      secondary: '#E1E1E1', 
      disabled: '#B0B0B0' 
    },
    background: {
      paper: '#151A1E',
      default: '#1D2226', // Main dark background
      neutral: alpha('#ffffff', 0.05),
      surface: '#2B2E33',
      elevated: '#17201C',
    },
    action: { 
      active: '#E1E1E1', 
      ...JLR_COMMON.action,
      hover: alpha('#ffffff', 0.04),
      selected: alpha('#ffffff', 0.08),
      disabled: alpha('#ffffff', 0.26),
      disabledBackground: alpha('#ffffff', 0.12),
      focus: alpha('#ffffff', 0.12),
    },
    divider: '#595959',
    shadows: {
      primary: alpha('#000000', 0.25),
      secondary: alpha('#c8b99c', 0.08),
      elevated: '0px 0px 4px rgba(0, 0, 0, 0.25)',
    },
  },
};